import React, { useMemo, useState } from 'react'
import { CancelBtn } from 'components/Button/CancelBtn'
import { Input } from 'components/Input/Input'
import { NavBar } from 'components/NavBar/NavBar'
import {
  Animation,
  Box,
  Button,
  ButtonsContainer,
  Flex,
  H1WithBack,
  ItemsGrid,
  PageFooter,
  PageWrapper,
  SquareIcon,
  ThinSeparator,
} from 'styling/GlobalStyles'
import { useDispatch } from 'react-redux'
import { AppDispatch } from 'store'
import { ISelectValue } from 'constants/globalTypes'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { IAddInventoryRequestFormState } from 'types/inventoryRequests.interface'
import { addInventoryRequest } from 'features/inventory/inventoryRequestsSlice'
import { HonestSelectAsyncInput } from 'components/HonestAsyncSelect/HonestAsyncSelectInput'
import { BodyText, TitleText } from 'styling/Typography'
import { PlusIcon } from 'assets/icons/PlusIcon'

export const AddInventoryRequest = () => {
  const [state, setState] = useState<IAddInventoryRequestFormState>({
    department: null,
    costCenter: null,
    notes: '',
    lines: [
      {
        id: new Date().getMilliseconds(),
        item: null,
        quantity: null,
      },
    ],
  })
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()

  const usedItemIds = useMemo(
    () => state.lines.map((l) => l.item?.value).filter(Boolean),
    [state.lines],
  )

  const handleSubmit = async (e: React.MouseEvent) => {
    e.preventDefault()
    if (
      !state.department?.value ||
      state.lines.some((l) => !l.item?.value || !l.quantity || l.quantity < 0.01)
    ) {
      toast.warn('All fields must be valid')
      return
    }
    if (usedItemIds.length !== new Set(usedItemIds).size) {
      toast.warn('Each item can only be added once')
      return
    }
    const resp = await dispatch(addInventoryRequest(state))
    if (resp.meta.requestStatus === 'fulfilled') {
      navigate('/inventory/requests')
    }
  }

  useFocusFirstInput()

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setState((prev) => ({ ...prev, [name]: value }))
  }

  const handleSelect = (value: ISelectValue, name: string) => {
    setState((prev) => ({ ...prev, [name]: value }))
  }

  const handleLineSelect = (value: ISelectValue, name: string, id: number) => {
    const copy = structuredClone(state)
    const lineIndex = copy.lines.findIndex((l) => l.id === id)
    if (lineIndex > -1) {
      copy.lines[lineIndex][name] = value
      setState(copy)
    }
  }

  const handleLineQuantity = (e: React.ChangeEvent<HTMLInputElement>, id: number) => {
    const { valueAsNumber } = e.target
    const copy = structuredClone(state)
    const lineIndex = copy.lines.findIndex((l) => l.id === id)
    if (lineIndex > -1) {
      copy.lines[lineIndex].quantity = valueAsNumber
      setState(copy)
    }
  }

  const handleAddLine = () => {
    setState((prev) => ({
      ...prev,
      lines: [
        ...prev.lines,
        {
          id: new Date().getMilliseconds(),
          item: null,
          quantity: null,
        },
      ],
    }))
  }

  return (
    <>
      <NavBar />
      <Animation>
        <PageWrapper>
          <H1WithBack mb='1' arrow title='Add Inventory Request' />
          <ItemsGrid columns={2}>
            <HonestSelectAsyncInput
              nomarg
              link='departments'
              placeholder='Search department'
              name='department'
              labelText='Department'
              defaultValue={state.department?.label}
              onChange={handleSelect}
              parserFunction={(data: any) =>
                data.data.map((i: any) => ({ label: i.name, value: i.id }))
              }
              invalid={!state.department?.value}
              required
            />
            <HonestSelectAsyncInput
              nomarg
              link='inventory/cost-center'
              name='costCenter'
              labelText='Cost Center'
              placeholder='Select cost center'
              defaultValue={state.costCenter?.label}
              onChange={handleSelect}
              parserFunction={(data: any) =>
                data.data.map((i: any) => ({ label: i.name, value: i.id }))
              }
            />
          </ItemsGrid>
          <Input
            name='notes'
            labelText='Notes'
            placeholder='Enter notes'
            value={state.notes}
            onChange={handleChange}
            type='textarea'
            height='100px'
          />
          <ThinSeparator />
          <Flex mb='16' width='20%' between>
            <TitleText weight='bold'>Items</TitleText>
            <SquareIcon onClick={handleAddLine}>{PlusIcon}</SquareIcon>
          </Flex>
          {state.lines.length === 0 ? (
            <BodyText>No items added yet.</BodyText>
          ) : (
            state.lines.map((l, i) => (
              <Box key={l.id}>
                <Flex column>
                  <BodyText weight='bold'>Item {i + 1}</BodyText>
                  <ItemsGrid mt='1rem' columns={2}>
                    <HonestSelectAsyncInput
                      name='item'
                      labelText='Item'
                      onChange={(v, n) => handleLineSelect(v, n, l.id)}
                      link='inventory/item'
                      defaultValue={l.item?.label}
                      parserFunction={(data: any) =>
                        data.data.map((item: any) => ({
                          label: item.name,
                          value: item.id,
                        }))
                      }
                      invalid={!l.item?.value}
                      required
                    />
                    <Input
                      name='quantity'
                      labelText='Quantity'
                      type='number'
                      placeholder='Enter quantity'
                      value={l.quantity}
                      onChange={(e) => handleLineQuantity(e, l.id)}
                      min={0.01}
                      step={0.01}
                      required
                    />
                  </ItemsGrid>
                </Flex>
              </Box>
            ))
          )}
        </PageWrapper>
        <PageFooter>
          <ButtonsContainer>
            <CancelBtn />
            <Button orange onClick={handleSubmit}>
              Save
            </Button>
          </ButtonsContainer>
        </PageFooter>
      </Animation>
    </>
  )
}
